import errorReporter from '@app/errorReporter';
import { Page } from 'components';
import { useBreakpoint } from 'hooks';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps, useParams, useHistory } from 'react-router-dom';
import { featureEnabled } from 'utils';
import { useSearchParams } from './hooks/useSearchParams';
import { ShowingsCalendarContextProvider } from './ShowingsCalendarContext';
import ShowingsCalendarInitialActionsWrapper from './ShowingsCalendarInitialActionsWrapper';
import ShowingsCalendarTabs from './ShowingsCalendarTabs';
import ShowingHeader from './ShowingToolBar/ShowingHeader';
import ShowingInfoModal from './ShowingInfoModal';
import { ES_DELAY } from './constants';
import {
  CalendarType,
  isCalendarType,
  ShowingsCalendarPageParams,
  ShowingCalendarSearchParams,
} from './types';
import { AgentUserCalendarContextProvider } from './AgentUserCalendarContext';
import { ShowingsCalendarHoverContextProvider } from './ShowingsCalendarHoverContext';

export interface ShowingsCalendarPageProps extends RouteComponentProps {
  calendarType: CalendarType;
}

const ShowingsCalendarPage = (props: ShowingsCalendarPageProps) => {
  const { calendarType } = props;
  const { md } = useBreakpoint();
  const { listingId } = useParams<ShowingsCalendarPageParams>();
  const { getSearchParam } = useSearchParams<ShowingCalendarSearchParams>();
  if (!isCalendarType(calendarType)) {
    errorReporter.error(`Invalid Showings Calendar type ${calendarType}`);
  }
  if (calendarType === 'listing' && !listingId) {
    errorReporter.error(
      'No listingId provided for individual listing calendar',
    );
  }
  const showHeader = React.useMemo(
    () => !(!md && calendarType !== 'listing'),
    [calendarType, md],
  );
  const [toast, wait, apptInfo] = getSearchParam('toast', 'wait', 'apptInfo');
  const history = useHistory();
  // Existence of toast implies an action was done so we need to wait for changes to propagate to ES
  let sleep = toast ? 'LARGE' : undefined;
  if (wait) {
    sleep = wait in ES_DELAY ? wait : 'LARGE';
  }

  const [showModal, setShowModal] = useState(false);
  const [showingInfo, setShowingInfo] = useState('');
  useEffect(() => {
    if (apptInfo) {
      setShowingInfo(apptInfo);
      setShowModal(true);
      history.replace('/my_business/showings/buyer');
    }
  }, []);
  return (
    <ShowingsCalendarContextProvider
      calendarType={calendarType}
      wait={sleep as keyof typeof ES_DELAY}
    >
      <AgentUserCalendarContextProvider>
        <ShowingsCalendarHoverContextProvider>
          {featureEnabled('showingsMadeEasyPhase2Feature') && showModal && (
            <ShowingInfoModal
              showModal={showModal}
              setShowModal={setShowModal}
              apptInfo={showingInfo}
            />
          )}
          <Page
            type="filled"
            headerExtra={showHeader && <ShowingHeader />}
            fullTitleWidth
            fillScreenHeight
            headerVerticalAlign={'baseline'}
            headerDirection="column"
          >
            <ShowingsCalendarInitialActionsWrapper>
              <ShowingsCalendarTabs />
            </ShowingsCalendarInitialActionsWrapper>
          </Page>
        </ShowingsCalendarHoverContextProvider>
      </AgentUserCalendarContextProvider>
    </ShowingsCalendarContextProvider>
  );
};

export default ShowingsCalendarPage;
